import uuid from 'uuid'
import Vue from 'vue'
import _ from 'lodash'
import { getField, updateField } from 'vuex-map-fields'
import {
  getInputConversionFunction,
  getTeamObjectBy,
  getHomebaseConfigData,
  getStatsLabel,
  getStandingsLabel,
  getOutputModelForComponent,
  getHomebaseIdList,
  getHomebaseIdData,
  getDataFromStorage,
} from './util/mashup'

const defaultConfig = () => ({
  hideSchedule: null,
  usePostseasonSchedule: null,
  showSeriesStatusRow: null,
  hideTicketsGames: null,
  statsMode: null,
  statsSeason: null,
  hideMoreInfoSchedule: null,
  moreInfoScheduleMonth: null,
  moreInfoScheduleYear: null,
  hideMoreInfoResults: null,
  moreInfoResultsMonth: null,
  moreInfoResultsYear: null,
  moreInfoBallparkId: null,
  moreInfoBallparkName: null,
})

const INITIAL_MASHUP_STATE = {
  androidConfig: defaultConfig(),
  iosConfig: defaultConfig(),
  insiderOrder: [],
  insider: {},
  moreFromOrder: [],
  moreFrom: {},
}

const LOCALES = ['en', 'es']
const DEVICES = ['phone', 'tablet']
const PLATFORMS = ['ios', 'android']

const modelReducer = (obj, options) => {
  const { locale, platform, device } = options

  return (arr, id) => {
    const currentObj = obj[id]
    const shouldInclude = currentObj.locale === locale &&
      currentObj.platforms.includes(platform) &&
      currentObj.devices.includes(device)

    if (shouldInclude) {
      arr.push(getOutputModelForComponent(currentObj, options))
    }

    return arr
  }
}

export default {
  state: Object.assign({}, INITIAL_MASHUP_STATE, {
    teamId: 'mlb',
    locale: 'en',
    platform: 'ios',
    device: 'phone',
  }),
  getters: {
    getField,
    getAndroidConfigField(state) {
      return getField(state.androidConfig)
    },
    getIosConfigField(state) {
      return getField(state.iosConfig)
    },
    insiderOrder: (state) => state.insiderOrder,
    insider: (state) => state.insider,
    insiderOrdered: (state) => state.insiderOrder.map((id) => state.insider[id]),
    moreFromOrder: (state) => state.moreFromOrder,
    moreFrom: (state) => state.moreFrom,
    moreFromOrdered: (state) => state.moreFromOrder.map((id) => state.moreFrom[id]),
    paths: (state, getters) => Object.keys(getters.model).map((path) => `atbatconfig/mashup/${path}/${state.teamId}.json`),
    raw: (state) => {
      const {
        androidConfig,
        iosConfig,
        insider,
        insiderOrder,
        moreFrom,
        moreFromOrder,
      } = state

      return {
        androidConfig,
        iosConfig,
        insider,
        insiderOrder: insiderOrder,
        moreFrom,
        moreFromOrder: moreFromOrder,
      }
    },
    model: (state) => {
      const {
        androidConfig,
        iosConfig,
        insider,
        insiderOrder,
        moreFrom,
        moreFromOrder,
      } = state

      const files = {}

      LOCALES.forEach((locale) => {
        PLATFORMS.forEach((platform) => {
          DEVICES.forEach((device) => {
            const key = `${locale}/${platform}/${device}`
            const options = {
              locale,
              device,
              platform,
            }

            files[key] = {
              config: platform === 'ios' ? iosConfig : androidConfig,
              insider: insiderOrder.reduce(modelReducer(insider, options), []),
              more: moreFromOrder.reduce(modelReducer(moreFrom, options), []),
            }
          })
        })
      })

      return files
    },
  },
  mutations: {
    updateField,
    updateAndroidConfigField(state, field) {
      updateField(state.androidConfig, field)

      if (field.path === 'statsMode') {
        state.androidConfig.statsLabel = getStatsLabel(state.androidConfig.statsMode)
      }
    },
    updateIosConfigField(state, field) {
      updateField(state.iosConfig, field)

      if (field.path === 'statsMode') {
        state.iosConfig.statsLabel = getStatsLabel(state.iosConfig.statsMode)
      }
    },
    addShortContentToType(state, type) {
      const id = uuid.v4()

      Vue.set(state[type], id, {
        id,
        type: 'short-content',
        title: 'New Short Content',
        url: null,
        popup: false,
        imageUrl: null,
        imagePublicId: null,
        platforms: ['ios', 'android'],
        devices: ['phone', 'tablet'],
        locale: state.locale,
      })

      state[`${type}Order`].push(id)
    },
    addApplicationPromoToType(state, type) {
      const id = uuid.v4()

      Vue.set(state[type], id, {
        id,
        type: 'application-promo',
        title: 'New App Item',
        blurb: null,
        iosUrl: null,
        androidUrl: null,
        iosPackage: null,
        androidPackage: null,
        intent: null,
        external: false,
        imageUrl: null,
        imagePublicId: null,
        platforms: ['ios', 'android'],
        devices: ['phone', 'tablet'],
        locale: state.locale,
        cloudinary: true,
      })

      state[`${type}Order`].push(id)
    },
    removeItemFromTypeById(state, { id, type }) {
      const orderIndex = state[`${type}Order`].indexOf(id)

      if (orderIndex >= 0) {
        delete state[type][id]
        state[`${type}Order`].splice(orderIndex, 1)
      }
    },
    resetTypeState(state, type) {
      state[`${type}Order`] = []
      state[type] = {}
    },
    updateTypeOrder(state, { order, type }) {
      state[`${type}Order`] = order
    },
    updateTypeField(state, { id, node, value, type }) {
      const element = state[type][id]

      if (element) {
        if (node === 'imageUrl' && value && element.hbImages) {
          delete element.hbImages
        }
        element[node] = value
      }
    },
    setMashupData(state, data) {
      Object.assign(state, data)
    },
    resetMashupData(state) {
      Object.assign(state, INITIAL_MASHUP_STATE)
    },
  },
  actions: {
    async loadMashupData({ commit }, options = {}) {
      const { teamId } = options
      const initialState = {
        androidConfig: {},
        iosConfig: {},
        insider: {},
        insiderOrder: [],
        moreFrom: {},
        moreFromOrder: [],
      }

      commit('resetMashupData')

      const stateFromStorage = await getDataFromStorage(teamId)

      if (stateFromStorage) {
        if (stateFromStorage.config && !stateFromStorage.androidConfig && !stateFromStorage.iosConfig) {
          console.log("Copying legacy shared platform config to separate iOS and Android config")
          stateFromStorage.androidConfig = _.cloneDeep(stateFromStorage.config)
          stateFromStorage.iosConfig = _.cloneDeep(stateFromStorage.config)
          delete stateFromStorage.config
        }
        return commit('setMashupData', stateFromStorage)
      }

      const teamObject = getTeamObjectBy('fileCode', teamId)

      let config = await getHomebaseConfigData(teamObject.fileCode)
      config = Object.assign(config, {
        statsLabel: getStatsLabel(config.statsMode),
        standingsLabel: getStandingsLabel(teamId),
      })
      initialState.androidConfig = _.cloneDeep(config)
      initialState.iosConfig = _.cloneDeep(config)

      await Promise.all(['insider', 'moreFrom'].map(async (type) => {
        return await Promise.all(['en', 'es'].map(async (locale) => {
          const ids = await getHomebaseIdList({ type, locale, teamId: teamObject.id })
          const hits = await getHomebaseIdData(ids)

          if (!hits.length) {
            return
          }

          initialState[`${type}Order`] = initialState[`${type}Order`].concat(ids)
          Object.assign(initialState[type], hits.reduce((obj, hitWrapper) => {
            const { hit, hit: { id, type } } = hitWrapper
            const convertInput = getInputConversionFunction(type)
            const modeledInput = convertInput(hit, { locale })

            return Object.assign(obj, modeledInput ? {
              [id]: modeledInput,
            } : {})
          }, {}))
        }))
      }))

      return commit('setMashupData', initialState)
    },
  },
}
